import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Tab } from '@mui/material';
import React, { memo, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import { CreateCompanyFormValues } from '~/types/formValues';

import styles from './styles.module.scss';

const DRAGGABLE_SUBROUND = 'SUBROUND';

type SubroundData = CreateCompanyFormValues['form']['rounds'][number]['subrounds'][number];

interface Props {
  index: number;
  subroundData: SubroundData;
  swapping: boolean;
  companyLocked: boolean;
  subroundsLength: number;
  roundIndex: number;
  roundName: string;
  onRemoveClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>, id: string) => void;
  onSubroundsSort: (
    roundIndex: number,
    dragIndex: number,
    dragData: SubroundData,
    hoverIndex: number,
    hoverData: SubroundData,
  ) => void;
  onSubroundsSwap: (
    dragRoundIndex: number,
    dragIndex: number,
    dragData: SubroundData,
    dropRoundIndex: number,
    dropIndex: number,
    dropData: SubroundData,
  ) => void;
}

const Component: React.FC<Props> = ({
  index,
  subroundData,
  swapping,
  companyLocked,
  subroundsLength,
  roundIndex,
  roundName,
  onRemoveClick,
  onSubroundsSort,
  onSubroundsSwap,
  ...tabsChildProps
}) => {
  const tabRef = useRef<HTMLDivElement | null>(null);
  const [{ handlerId }, drop] = useDrop<
    { roundIndex: number; index: number; data: SubroundData },
    void,
    { handlerId: string | symbol | null }
  >({
    accept: DRAGGABLE_SUBROUND,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      const dragRoundIndex = item.roundIndex;
      const hoverRoundIndex = roundIndex;

      // sorting should work in the same round
      if (dragRoundIndex !== hoverRoundIndex) {
        return;
      }

      if (!tabRef.current) {
        return;
      }

      const dragIndex = item.index;
      const dragData = item.data;
      const hoverIndex = index;
      const hoverData = subroundData;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = tabRef.current.getBoundingClientRect();

      // Get horizontal middle
      const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the left
      const hoverClientX = (clientOffset?.x || 0) - hoverBoundingRect.left;

      // Only perform the move when the mouse has crossed half of the items width
      // When dragging left, only move when the cursor is below 50%
      // When dragging right, only move when the cursor is above 50%

      // Dragging left
      if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) {
        return;
      }

      // Dragging right
      if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) {
        return;
      }

      onSubroundsSort(item.roundIndex, dragIndex, dragData, hoverIndex, hoverData);

      item.index = hoverIndex;
    },
    drop(item) {
      const dragRoundIndex = item.roundIndex;
      const dropRoundIndex = roundIndex;

      // drop should work in different rounds
      if (dragRoundIndex === dropRoundIndex) {
        return;
      }

      const dragIndex = item.index;
      const dragData = item.data;
      const dropIndex = index;
      const dropData = subroundData;

      onSubroundsSwap(dragRoundIndex, dragIndex, dragData, dropRoundIndex, dropIndex, dropData);
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: DRAGGABLE_SUBROUND,
    item: () => {
      return { roundIndex, index, data: subroundData };
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  if (swapping) {
    drag(drop(tabRef));
  }

  return (
    <Tab
      data-handler-id={handlerId}
      disableRipple={swapping}
      icon={
        swapping ? (
          <DragIndicatorIcon
            sx={{
              width: '16px',
              height: '16px',
              color: 'var(--gray-6-color)',
              mr: '8px !important',
            }}
          />
        ) : !companyLocked && subroundsLength > 1 ? (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <div
            aria-label="Remove"
            className={styles['tab__remove-button']}
            role="button"
            tabIndex={-1}
            onClick={event => onRemoveClick(event, subroundData.id)}
          >
            <CloseIcon sx={{ color: 'var(--gray-2-color)', width: '20px', height: '20px' }} />
          </div>
        ) : undefined
      }
      iconPosition={swapping ? 'start' : 'end'}
      label={`Round ${roundName}${index + 1}`}
      ref={tabRef}
      sx={{
        minHeight: 'initial',
        height: '48px',
        cursor: swapping ? 'move' : undefined,
        opacity: isDragging ? '0' : '1',
        '&:hover': swapping
          ? {
              borderLeft: '2px solid var(--main-2-color)',
            }
          : undefined,
      }}
      title={swapping ? 'Move' : undefined}
      {...tabsChildProps}
    />
  );
};

export const DraggableTab = memo(Component);
